import { MenuModel, RouteCodeEnum } from '@models/menu';

export type CipoTheme = 'default' | 'orange';
export type CipoThemeMode = 'dark' | 'light' | 'browser';
export type Theme = {
  color: CipoTheme;
  mode: CipoThemeMode;
};

export type A11yContrast = 'normal' | 'high';
export type A11yFontScale = 'normal' | 'lg' | 'xl';
export type A11yTextSpacing = 'normal' | 'comfortable' | 'spacious';
export type A11yLineHeight = 'normal' | 'comfortable' | 'spacious';
export type A11ySaturation = 'normal' | 'low' | 'high' | 'desaturate';
export type A11yColorFilter = 'normal' | 'protanopia' | 'deuteranopia' | 'tritanopia';
export type A11yReadingAid = 'off' | 'guide' | 'mask';
export type A11yPreferences = {
  contrast: A11yContrast;
  reduceMotion: boolean;
  fontScale: A11yFontScale;
  textSpacing: A11yTextSpacing;
  lineHeight: A11yLineHeight;
  saturation: A11ySaturation;
  underlineLinks: boolean;
  dyslexiaFont: boolean;
  colorFilter: A11yColorFilter;
  largeCursor: boolean;
  readingAid: A11yReadingAid;
};

// Applied to <body> so CDK overlays share the theme and accessibility preferences.
export function applyAppearanceToBody(theme: Theme, preferences: A11yPreferences) {
  Array.from(document.body.classList)
    .filter(className => className.endsWith('-theme') || className.endsWith('-mode'))
    .forEach(className => document.body.classList.remove(className));

  // Both appearances use the light palette; Dark is applied once by the inversion filter.
  document.body.classList.add(theme.color + '-theme', 'light-mode');
  appendA11yClassesToBody(preferences, theme.mode);
}

export function resolveThemeMode(mode: CipoThemeMode): 'light' | 'dark' {
  if (mode !== 'browser') return mode;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// Applied to <body> (not app-root) so CDK overlays also receive a11y classes.
// `colorFilter` and `readingAid` are excluded: they are owned by dedicated
// components that handle the absolute-URL SVG filter and pointer overlay directly.
function appendA11yClassesToBody(
  {
    contrast,
    reduceMotion,
    fontScale,
    textSpacing,
    lineHeight,
    saturation,
    underlineLinks,
    dyslexiaFont,
    largeCursor,
  }: A11yPreferences,
  mode: CipoThemeMode,
) {
  Array.from(document.body.classList)
    .filter(className => className.startsWith('a11y-'))
    .forEach(className => document.body.classList.remove(className));

  if (contrast === 'high') {
    document.body.classList.add('a11y-contrast-high');
  }
  if (resolveThemeMode(mode) === 'dark') {
    document.body.classList.add('a11y-contrast-invert');
  }
  if (reduceMotion) {
    document.body.classList.add('a11y-reduce-motion');
  }
  if (fontScale !== 'normal') {
    document.body.classList.add(`a11y-font-${fontScale}`);
  }
  if (textSpacing !== 'normal') {
    document.body.classList.add(`a11y-text-spacing-${textSpacing}`);
  }
  if (lineHeight !== 'normal') {
    document.body.classList.add(`a11y-line-height-${lineHeight}`);
  }
  if (saturation !== 'normal') {
    document.body.classList.add(`a11y-saturation-${saturation}`);
  }
  if (underlineLinks) {
    document.body.classList.add('a11y-underline-links');
  }
  if (dyslexiaFont) {
    document.body.classList.add('a11y-dyslexia-font');
  }
  if (largeCursor) {
    document.body.classList.add('a11y-large-cursor');
  }
}

export function getMenuItems(menuItems: MenuModel[]): MenuModel[] {
  return menuItems.reduce((acc: MenuModel[], item) => {
    acc.push(item);
    if (item.children) {
      acc.push(...getMenuItems(item.children));
    }

    return acc;
  }, []);
}

export function findMenuItemByCode(menuItems: MenuModel[], code: RouteCodeEnum): MenuModel | undefined {
  for (const item of menuItems) {
    if (item.code === code) {
      return item;
    }
  }
  return undefined;
}
